import React, { useCallback, useMemo } from 'react';
import { draw } from 'actions/cards';
import classnames from 'classnames';

import { withDeckRefreshContext } from 'components/DeckRefreshContext';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { IAppState } from 'types/app';

import DeckComponent from './DeckComponent';

const DECK_IMAGE_URLS = {
  default: '//images.fallenlondon.com/cards/deck.png',
  disabled: '//images.fallenlondon.com/cards/deck-disabled.png',
  empty: '//images.fallenlondon.com/cards/refill-deck-for-nex.png',
};

type Props = ReturnType<typeof mapStateToProps> & {
  dispatch: Function,
  onOpenDeckRefreshModal: () => void,
};

function Deck({
  cardsCount,
  dispatch,
  displayCards,
  handSize,
  isFetching,
  onOpenDeckRefreshModal,
}: Props) {
  const handFull = (displayCards?.length ?? 0) === handSize;

  const noCards = useMemo(() => cardsCount === 0 && !isFetching, [cardsCount, isFetching]);

  const drawCards = useCallback(() => {
    dispatch(draw());
  }, [dispatch]);

  const imageUrl = useMemo(() => {
    if (noCards) {
      return DECK_IMAGE_URLS.empty;
    }
    if (handFull) {
      return DECK_IMAGE_URLS.disabled;
    }
    return DECK_IMAGE_URLS.default;
  }, [handFull, noCards]);

  const topUpCards = useCallback(() => { onOpenDeckRefreshModal() }, [onOpenDeckRefreshModal]);

  const handleClick = useCallback(() => {
    // If our deck is empty, allow the player to top up
    if (noCards) {
      topUpCards();
      return;
    }

    // If our hand is full, just return
    if (handFull) {
      return;
    }

    // Otherwise, draw cards
    drawCards();
  }, [
    drawCards,
    handFull,
    noCards,
    topUpCards,
  ]);

  const imageSrc = imageUrl;

  const deckClassName = classnames(
    'deck',
    isFetching && 'deck--fetching',
    handFull && !noCards && 'deck--full',
    noCards && 'deck--empty',
  );

  const imageClassName = classnames(
    'deck__image',
    handFull && !noCards && 'deck__image--disabled',
  );

  return (
    <DeckComponent
      deckClassName={deckClassName}
      imageClassName={imageClassName}
      imageSrc={imageSrc}
      onClick={handleClick}
    />
  );
}

Deck.displayName = 'Deck';

const mapStateToProps = ({
  cards,
  fate: { data: fateData },
}: IAppState) => ({
  ...cards,
  fateData,
});

export default withRouter(connect(mapStateToProps)(withDeckRefreshContext(Deck)));
